import { useRef, useState } from "react";
import { toast } from "sonner";
import { Receipt, Upload, Trash2 } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";

const MAX = 5;
const CAN_UPLOAD = ["ordered", "arrived_pastelfix", "sorting", "shipped_areku", "arrived_areku", "packing"];

const Thumb = ({ drop, r, isAdmin, onDelete }) => {
  const src = `${API}/drops/${drop.id}/receipts/${r.id}`;
  return (
    <div className="relative group">
      <a href={src} target="_blank" rel="noreferrer" data-testid={`receipt-${r.id}`}>
        <img src={src} alt="Proof of purchase receipt" className="h-32 w-full object-cover rounded-xl border border-white/10 bg-zinc-900 transition-transform group-hover:scale-[1.02]" />
      </a>
      {isAdmin && (
        <button type="button" data-testid={`receipt-delete-${r.id}`} onClick={() => onDelete(r)} aria-label="Remove receipt"
          className="absolute top-1.5 right-1.5 rounded-full bg-zinc-950/80 p-1.5 text-zinc-300 hover:text-red-300 transition-colors">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
};

export const ProofOfPurchase = ({ drop, isAdmin, onChange }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const receipts = drop.receipts || [];
  const canUpload = isAdmin && CAN_UPLOAD.includes(drop.status) && receipts.length < MAX;
  if (!receipts.length && !(isAdmin && CAN_UPLOAD.includes(drop.status))) return null;
  const upload = async (e) => {
    const picked = [...e.target.files];
    const files = picked.slice(0, MAX - receipts.length);
    e.target.value = "";
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/drops/${drop.id}/receipts`, fd);
      }
      if (picked.length > files.length) toast.warning(`Only ${files.length} added (max ${MAX} receipts)`);
      else toast.success(files.length > 1 ? "Receipts added" : "Receipt added");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
      onChange();
    }
  };
  const remove = async (r) => {
    if (!window.confirm("Remove this receipt?")) return;
    try {
      await api.delete(`/drops/${drop.id}/receipts/${r.id}`);
      onChange();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div data-testid="proof-of-purchase" className="mt-5 max-w-2xl rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.05] p-4">
      <div className="flex items-center gap-2">
        <Receipt className="h-4 w-4 text-emerald-300" />
        <span className="text-[11px] uppercase tracking-widest text-emerald-300 font-semibold">Proof of Purchase</span>
        {canUpload && (
          <button type="button" data-testid="receipt-upload-btn" disabled={busy} onClick={() => input.current?.click()}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 px-3 py-1 text-xs font-semibold text-emerald-200 transition-colors hover:bg-emerald-500/10 disabled:opacity-50">
            <Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : `Add receipt (${receipts.length}/${MAX})`}
          </button>
        )}
        <input ref={input} type="file" accept="image/*" multiple hidden data-testid="receipt-file-input" onChange={upload} />
      </div>
      {receipts.length > 0 ? (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
          {receipts.map((r) => <Thumb key={r.id} drop={drop} r={r} isAdmin={isAdmin} onDelete={remove} />)}
        </div>
      ) : (
        <p className="mt-2 text-xs text-zinc-400">No receipts yet. Add up to {MAX} images; joiners will see them here.</p>
      )}
    </div>
  );
};
